import { FC, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { INotice } from '../../../models/boardNotice.model';
import { doGetGroups } from '../../../store/group.slice';
import { doRemoveAuth } from '../../../store/auth.slice';
import { RootState } from '../../../store/rootReducer';
import _ from 'lodash';

import { useSnackbar } from 'notistack';
import { CgCloseR } from 'react-icons/cg';
import Loader from '../../Loader';
import Button from '../../Button';
import FileUpload from '../../FileUpload';
import SnackbarAction from '../../SnackbarAction';
import Radio from '../../Radio';
import Tinymce from '../../Tinymce';
import SelectCampusModal from '../../modals/SelectCampusModal2';
import SelectGroupModal from '../../modals/SelectGroupModal';
import useAuth from '../../../hooks/useAuth';
import MultiCampusModal from '../../statistics/regular-test/modals/MultiCampusModal';
import SelectCampusMultiChoice from '../../SelectCampusMultiChoice';

interface IProps {
  notice?: INotice|undefined;
  onSubmit: Function;
}

const BoardNoticeEditor: FC<IProps> = ({ notice, onSubmit }) => {
  const { writeAuthYn } = useAuth();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { groups, isLoading: groupLoading } = useSelector((state: RootState) => state.group);

  const [title, setTitle] = useState('');
  const [campusCodes, setCampusCodes] = useState<string[]>([]);
  const [group, setGroup] = useState<any>([]);
  const [groupData, setGroupData] = useState<any>([]);

  const [openYn, setOpenYn] = useState<any>('Y');
  const [content, setContent] = useState<any>();
  // 신규 첨부파일
  const [files, setFiles] = useState<any>([null]);
  // 수정 시 기존 첨부파일
  const [fileinfo, setFileinfo] = useState<any>([null]);

  const [multiCampusVisible, setMultiCampusVisible] = useState(false);
  const [selectGroupVisible, setSelectGroupVisible] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [groupPage, setGroupPage] = useState(1);
  const [selection, setSelection] = useState<any>([]);

  const titleInput = useRef<any>();

  useEffect(() => {
    if (groups) setGroupData(groups.filter((group)=> group));
  }, [groups]);

  useEffect(() => {
    if (notice) {
      setTitle(notice.title);
      setCampusCodes(notice.campusCode);
      setGroup(notice.groupCode);
      setOpenYn(notice.openYn);
      setContent(notice.content);
      setFileinfo(_.cloneDeep(notice.files));
    }
  }, [notice]);

  useEffect(() => {
    getGroups();
  }, []);

  const action = (id: string | number) => <SnackbarAction id={id} />;

  const getGroups = async () => {
    const res = await dispatch(doGetGroups());

    switch (res?.status) {
      case 200:
        break;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), {
          variant: 'warning',
        });
        history.push('/');
        break;
      default:
        enqueueSnackbar(res?.data?.data || t('common.message.error'), {
          variant: 'error',
          persist: true,
          action
        });
    }
  }

  const onSetFile = (index: number, file: any) => {
    const _files = [...files];
    _files[index] = file;
    setFiles(_files);
  }

  const onAddFile = () => {
    const _files = [...files];
    _files.push(null);
    setFiles(_files);
  }

  const onDeleteFile = (index: number) => {
    const _files = [...files];
    _files.splice(index, 1);
    setFiles(_files);
  }

  const onSave = async () => {
    if (!title) {
      titleInput.current.focus();
      return enqueueSnackbar(t('student.student.message15'), {
        variant: 'info'
      });
    }

    const form = new FormData();
    form.append('title', title);
    form.append('content', content);
    form.append('openYn', openYn);

    if (campusCodes?.length) {
      form.append('allCampusYn', 'N');
      campusCodes.forEach((campusCode: string) => form.append('campusCode', campusCode));
    } else {
      form.append('allCampusYn', 'Y');
    }

    if (group?.length > 0) {
      form.append('allGroupYn', 'N');
      group.forEach((item: any) => form.append('groupCode', item));
    } else {
      form.append('allGroupYn', 'Y');
    }

    if (notice) {
      // 수정 시 파일 처리
      if (notice.attachFileCode) {
        form.append('attachFileCode', notice.attachFileCode);
      }

      if (fileinfo) {
        // 기존 첨부파일 있음
        let hasNewFiles = false;
        files.forEach((item: any) => {
          if (item) hasNewFiles = true;
        });

        let isAllDeleted = true;
        let hasDeleted = false;
        fileinfo.forEach((item: any) => {
          if (item.originalName) {
            isAllDeleted = false;
          } else {
            hasDeleted = true;
            form.append('deleteFiles', item.fileSeq);
          }
        });

        if (hasNewFiles) form.append('fileStatus', 'update');
        else if (isAllDeleted) form.append('fileStatus', 'delete');
        else if (hasDeleted) form.append('fileStatus', 'update');
        else form.append('fileStatus', 'keep');
      } else {
        // 기존 첨부파일 없음
        if (files.length > 0 && files[0] !== null) {
          // 추가 첨부파일 있음
          form.append('fileStatus', 'create');
        } else {
          // 추가 첨부파일 없음
          form.append('fileStatus', 'keep');
        }
      }

      if (files.length > 0) {
        files.forEach((item: any) => {
          if (item !== null) form.append('newFiles', item);
        });
      }
    } else {
    // 생성 시 파일 처리
      if (files.length > 0) {
        files.forEach((item: any) => {
          if (item !== null) form.append('files', item);
        });
      }
    }

    onSubmit(form);
  }

  const onSetSelection = (id: string | number) => {
    if (selection.includes(id)) {
      setSelection(selection.filter((item: string) => item !== id));
    } else {
      setSelection([...selection, id]);
    }
  }

  const onSearchGroup = () => {
    let data: any = _.cloneDeep(groups);

    if (groupName) {
      data = data.filter((item: any) => item.korName.indexOf(groupName) !== -1);
    }
    setGroupData(data);
  }

  const onDeleteAttachedFile = (index: number) => {
    const _fileinfo = [...fileinfo];
    _fileinfo[index].originalName = null;
    setFileinfo(_fileinfo);
  }

  return (
    <div className='comm-table__area rowType edit'>
      {groupLoading ? <Loader /> : null}
      <table>
        <colgroup>
          <col width="15%" />
          <col width="85%" />
        </colgroup>
        <tbody>
          <tr>
            <th>{`* ${t('common.column.title')}`}</th>
            <td>
              <input
                type="text"
                className="form-control input-text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                ref={titleInput}
              />
            </td>
          </tr>
          <tr>
            <th>{t('board.notice.column5')}</th>
            <td className="d-flex">
              <div className="comm-btn__area">
                <SelectCampusMultiChoice
                  placeholder={t('statistics.eLearning.title2')}
                  onChange={setCampusCodes}
                  value={campusCodes}
                />
              </div>
              <div className="v-line ml-2">
                {
                  campusCodes?.length
                    ? `(${campusCodes.length}${t('board.notice.column9')})`
                    : `(${t('common.text.all')})`
                }
              </div>
            </td>
          </tr>
          <tr>
            <th>{t('board.notice.column6')}</th>
            <td className="d-flex">
              <div className="comm-btn__area">
                <Button
                  className="btn-basic mint"
                  onClick={() => {
                    group ? setSelection(group) : setSelection([]);
                    setSelectGroupVisible(true);
                  }}
                  text={t('board.notice.title7')}
                />
              </div>
              <div className="v-line ml-2">
                {
                  group?.length > 0
                    ? `(${group.length}${t('board.notice.column9')})`
                    : `(${t('common.text.all')})`
                }
              </div>
            </td>
          </tr>
          <tr>
            <th>{t('board.notice.column2')}</th>
            <td>
              <Radio
                checked={openYn === 'Y'}
                onChange={() => setOpenYn('Y')}
                text={t('common.option.yes')}
              />
              <Radio
                className="ml-4"
                checked={openYn === 'N'}
                onChange={() => setOpenYn('N')}
                text={t('common.option.no')}
              />
            </td>
          </tr>

          <tr>
            <th>{t('common.column.contents')}</th>
            <td>
              <Tinymce
                content={content}
                setContent={setContent}
              />
            </td>
          </tr>
          {
            notice && fileinfo
              ? (
                <tr>
                  <th>{t('archive.learn.column5')}</th>
                  <td colSpan={3}>
                    {
                      fileinfo.map((item: any, index: number) => {
                        const result = item?.originalName
                          ? (
                            <div key={index}>
                              <a
                                className="download-link"
                                href={`${item.fileLocation}`}
                                download={item.originalName}
                              >
                                {item.originalName}
                              </a>
                              <span
                                className="ml-1 pointer"
                                onClick={() => onDeleteAttachedFile(index)}
                              >
                                <CgCloseR />
                              </span>
                            </div>
                          )
                          : null;
                        return result;
                      })
                    }
                  </td>
                </tr>
                )
              : null
          }
          <tr>
            <th>{notice && fileinfo ? t('archive.learn.column6') : t('archive.exam.column7')}</th>
            <td colSpan={3}>
              {
                files.length > 0
                  ? files.map((file: any, index: number) => (
                      <div className="flex mb-2" key={index}>
                        <FileUpload
                          file={files[index]}
                          setFile={(file: any) => onSetFile(index, file)}
                        />
                        <div className="comm-btn__area">
                          <Button
                            className="btn-basic small gray ml-2"
                            onClick={() => onDeleteFile(index)}
                            text={t('common.button.delete')}
                          />
                        </div>
                      </div>
                    ))
                  : null
              }
              <div className="comm-btn__area">
                <Button
                  className="btn-basic pink-line"
                  onClick={onAddFile}
                  text={t('common.button.add')}
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <div className="comm-bx__space">
        <div className="comm-btn__area right">
          <Button
            onClick={onSave}
            className="btn-basic pink"
            text={t('common.button.save')}
            writeAuthYn={writeAuthYn}
          />
          <Button
            onClick={() => history.push(`/board/notice${location.search}`)}
            className="btn-basic gray"
            text={t('common.button.cancel')}
          />
        </div>
      </div>

      {multiCampusVisible &&
        <MultiCampusModal
          visible={multiCampusVisible}
          setVisible={setMultiCampusVisible}
          campusCodes={campusCodes}
          setCampusCodes={setCampusCodes}
        />
      }
      
      {selectGroupVisible && (
        <SelectGroupModal
          selectGroupVisible={selectGroupVisible}
          setSelectGroupVisible={setSelectGroupVisible}
          group={group}
          setGroup={setGroup}
          groupName={groupName}
          setGroupName={setGroupName}
          onSearchGroup={onSearchGroup}
          groupData={groupData}
          groupPage={groupPage}
          setGroupPage={setGroupPage}
          selection={selection}
          setSelection={setSelection}
          onSetSelection={onSetSelection}
        />
      )}
    </div>
  )
}

export default BoardNoticeEditor;
